import React, { FC, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import csv from 'csvtojson';
import { Toast } from '../../../../../ui-kit';
import { useFTAImpactContext } from '../../../../contexts/fta-impact/FTAImpactContext';
import { AVAILABLE_YEARS } from '../../../../libs/constant';
import { useAuth } from '../../../../modules/auth';

const InputOutputDropZone: FC = () => {
  const { setDataTable, setYear, setReporter, setPartner } = useFTAImpactContext();
  const { currentUser } = useAuth();
  const reporterISO3User = currentUser?.economy;

  type CsvRow = Record<string, string | number | undefined>;

  const mapCsvToOriginalFormat = (data: CsvRow[]) =>
    data.map((row) => {
      const hs_code = getHsCode(row["hs_code"]) || getHsCode(row["hs"]) || "";

      return {
        original: {
          hs_code,
          tc: getNumeric(row["tc"]) || getNumeric(row["total_change"]),
          td: getNumeric(row["td"]),
        },
      };
    });

  // Parse numbers like "1,234.56", "  -98  ", "1.2e3"
  const getNumeric = (input?: string | number): number => {
    if (typeof input === "number") return input;
    if (!input) return 0;
    const cleaned = input.replace(/[^0-9.-]+/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Pad HS Code to 6 digits with leading zeroes
  const getHsCode = (input?: string | number): string => {
    if (!input) return "";
    const raw = String(input).replace(/\D/g, ""); // keep digits only
    const trimmed = raw.slice(0, 6); // avoid overly long values
    return trimmed.padStart(6, "0"); // pad only if < 6 digits  
  };

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async () => {
      const text = reader.result as string;

      try {
        const json = await csv({ delimiter: [",", ";"] }).fromString(text);

        // Required columns
        const requiredHeaders = ["hs", "total_change"];
        const actualHeaders = Object.keys(json[0] || {});
        // console.log('Actual Headers:', actualHeaders);

        // Check missing headers
        const missingHeaders = requiredHeaders.filter(header => !actualHeaders.includes(header));

        if (missingHeaders.length > 0) {
          throw new Error(`Missing required columns: ${missingHeaders.join(", ")}`);
        }

        // Map the CSV to the desired format
        const mappedJson = mapCsvToOriginalFormat(json);

        // console.log(mappedJson)

        //If alls checks out
        setYear(AVAILABLE_YEARS[0].toString());
        setReporter(process.env.REACT_APP_DEFAULT_REPORTER || reporterISO3User!);
        setPartner(process.env.REACT_APP_DEFAULT_PARTNER || 'WLD');
        setDataTable(mappedJson)

        // console.log('CSV to JSON:', json);
      } catch (error) {
        Toast(
          'CSV Parsing Error',
          'danger',
          `${error}`
        )
      }
    };

    reader.onerror = () => console.error('File reading has failed');
    reader.onabort = () => console.warn('File reading was aborted');
    reader.readAsText(file);
  }, []);

  const onDropRejected = useCallback((fileRejections) => {
    fileRejections.forEach(({ file, errors }) => {
      const errorMessages = errors.map((e) => e.message).join(', ');
      Toast(
        'Invalid file type',
        'danger',
        `File "${file.name}" was rejected: ${errorMessages}`
      )
    });
  }, []);


  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
    onDropAccepted,
    onDropRejected
  });

  return (
    <div className="container">
      <div
        {...getRootProps({
          className: 'dropzone dropzone-warning',
        })}
      >
        <input {...getInputProps()} />
        <div className="dz-message needsclick">
          <i className="ki-duotone ki-file-up fs-3x text-warning">
            <span className="path1"></span><span className="path2"></span>
          </i>
          <div className="ms-4">
            <h3 className="fs-5 fw-bold text-gray-900 mb-1">Drop the CSV file here or click to upload.</h3>
            <span className="fs-7 fw-semibold text-gray-500">Maximum file size 5MB. The file should only have two columns with product codes and export change : <b>hs</b> and <b>total_change</b>, with <b>hs</b> product codes in H4 version (2012) and <b>total_change</b> showing the sum of trade diversion and trade creation and product level. </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InputOutputDropZone;